import { createContext, useContext, useMemo, type ReactNode } from 'react'
import type { PermissionCode, ScopeType } from '@concours/shared'

/**
 * Permissions effectives de l'utilisateur côté interface (RBAC dynamique).
 * ⚠️ Confort d'affichage uniquement : masquer un bouton ne protège rien,
 * le backend vérifie chaque route et chaque ressource (cahier §11.3).
 *
 * Alimenté en phase 4 par GET /api/v1/auth/me/permissions.
 */
export interface PermissionSnapshot {
  /** Permissions valables partout. */
  global: readonly string[]
  /** Permissions limitées à un périmètre : { competition: { '<id>': [...] }, stage: {...} } */
  scoped?: Partial<Record<Exclude<ScopeType, 'global'>, Record<string, readonly string[]>>>
  version?: number
}

export interface PermissionScope {
  type: Exclude<ScopeType, 'global'>
  id: string
}

interface PermissionApi {
  can: (code: PermissionCode, scope?: PermissionScope) => boolean
  canAny: (codes: readonly PermissionCode[], scope?: PermissionScope) => boolean
  canAll: (codes: readonly PermissionCode[], scope?: PermissionScope) => boolean
  version: number
}

const PermissionContext = createContext<PermissionApi | null>(null)

export function createPermissionChecker(snapshot: PermissionSnapshot): PermissionApi {
  const global = new Set(snapshot.global)
  const scoped = new Map<string, Set<string>>()
  for (const [type, entries] of Object.entries(snapshot.scoped ?? {})) {
    for (const [id, codes] of Object.entries(entries ?? {}))
      scoped.set(`${type}:${id}`, new Set(codes))
  }

  /**
   * Sans périmètre : vrai si la permission est globale OU détenue sur au moins
   * un périmètre (pour afficher une entrée de menu, par ex.).
   * Avec périmètre : vrai si globale OU détenue sur ce périmètre précis.
   */
  const can = (code: PermissionCode, scope?: PermissionScope) => {
    if (global.has(code)) return true
    if (scope) return scoped.get(`${scope.type}:${scope.id}`)?.has(code) ?? false
    for (const codes of scoped.values()) if (codes.has(code)) return true
    return false
  }

  return {
    can,
    canAny: (codes, scope) => codes.some((code) => can(code, scope)),
    canAll: (codes, scope) => codes.every((code) => can(code, scope)),
    version: snapshot.version ?? 0,
  }
}

export function PermissionProvider({
  snapshot,
  children,
}: {
  snapshot: PermissionSnapshot
  children: ReactNode
}) {
  const api = useMemo(() => createPermissionChecker(snapshot), [snapshot])
  return <PermissionContext.Provider value={api}>{children}</PermissionContext.Provider>
}

export function usePermissions(): PermissionApi {
  const context = useContext(PermissionContext)
  if (!context) throw new Error('usePermissions doit être utilisé dans <PermissionProvider>.')
  return context
}

/** `useCan('concours.publish')` ou `useCan('notes.enter', { type: 'stage', id })`. */
export function useCan(code: PermissionCode, scope?: PermissionScope): boolean {
  return usePermissions().can(code, scope)
}

export interface CanProps {
  /** Une permission, ou plusieurs (toutes requises par défaut). */
  perm: PermissionCode | readonly PermissionCode[]
  mode?: 'all' | 'any'
  scope?: PermissionScope
  fallback?: ReactNode
  children: ReactNode
}

/** Affiche `children` seulement si l'utilisateur a les permissions. */
export function Can({ perm, mode = 'all', scope, fallback = null, children }: CanProps) {
  const { canAll, canAny } = usePermissions()
  const codes = typeof perm === 'string' ? [perm] : perm
  const allowed = mode === 'any' ? canAny(codes, scope) : canAll(codes, scope)
  return <>{allowed ? children : fallback}</>
}

/** Garde de page : rend `forbidden` (page 403) si les permissions manquent. */
export function PermissionGuard(props: Omit<CanProps, 'fallback'> & { forbidden: ReactNode }) {
  return <Can {...props} fallback={props.forbidden} />
}
